/**
 * Reviews Types for Customer Review System
 *
 * Types for managing customer reviews, business responses,
 * and review analytics.
 */

// ============================================================================
// Review Status
// ============================================================================

export type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REPORTED';

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  PENDING: 'Pending Review',
  APPROVED: 'Published',
  REJECTED: 'Rejected',
  REPORTED: 'Under Review',
};

export const REVIEW_STATUS_COLORS: Record<ReviewStatus, string> = {
  PENDING: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
  REPORTED: 'orange',
};

// ============================================================================
// Review Rating
// ============================================================================

export type ReviewRating = 1 | 2 | 3 | 4 | 5;

export const RATING_LABELS: Record<ReviewRating, string> = {
  1: 'Terrible',
  2: 'Poor',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

export const RATING_LABELS_PT: Record<ReviewRating, string> = {
  1: 'Pessimo',
  2: 'Ruim',
  3: 'Regular',
  4: 'Bom',
  5: 'Excelente',
};

// ============================================================================
// Review Entity
// ============================================================================

export interface Review {
  id: string;
  businessId: string;
  customerId: string;
  bookingId: string;

  // Review content
  rating: ReviewRating;
  comment: string | null;

  // Status
  status: ReviewStatus;

  // Business response
  businessResponse: string | null;
  respondedAt: Date | null;
  respondedBy: string | null;

  // Report info
  reportReason: string | null;
  reportedAt: Date | null;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Review List Item (for display)
// ============================================================================

export interface ReviewListItem {
  id: string;
  businessId: string;
  businessName: string;

  // Customer info
  customerId: string;
  customerName: string;
  customerPhone?: string;

  // Booking info
  bookingId: string;
  serviceName: string;
  serviceDate?: Date;

  // Review content
  rating: number;
  comment: string | null;

  // Status
  status: ReviewStatus;
  hasResponse?: boolean;

  // Business response
  businessResponse: string | null;
  respondedAt: Date | null;
  respondedBy?: string | null;

  // Report info
  reportReason?: string | null;
  reportedAt?: Date | null;

  // Timestamps
  createdAt: Date;
}

// ============================================================================
// Review Filters
// ============================================================================

export interface ReviewFilters {
  searchQuery: string;
  ratings: number[];
  statuses: ReviewStatus[];
  hasResponse: boolean | null;
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  sortBy: ReviewSortField;
  sortOrder: 'asc' | 'desc';
}

export type ReviewSortField = 'createdAt' | 'rating' | 'respondedAt' | 'customerName';

export const DEFAULT_REVIEW_FILTERS: ReviewFilters = {
  searchQuery: '',
  ratings: [],
  statuses: [],
  hasResponse: null,
  dateRange: {
    startDate: null,
    endDate: null,
  },
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

// Page size options for pagination
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ============================================================================
// Review Analytics
// ============================================================================

export interface ReviewAnalytics {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: Record<ReviewRating, number>;

  // Response metrics
  totalResponded: number;
  responseRate: number;
  avgResponseTimeHours: number;

  // Trend data (last 30 days)
  recentReviews: number;
  recentAverageRating: number;
  ratingTrend: 'up' | 'down' | 'stable';

  // Status breakdown
  byStatus: Record<ReviewStatus, number>;
}

// ============================================================================
// Review Request (WhatsApp)
// ============================================================================

export interface ReviewRequest {
  id: string;
  businessId: string;
  customerId: string;
  bookingId: string;

  // Request status
  sentAt: Date;
  completedAt: Date | null;
  expiresAt: Date;

  // WhatsApp message ID
  whatsappMessageId: string | null;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CreateReviewRequest {
  bookingId: string;
  rating: ReviewRating;
  comment?: string;
}

export interface RespondToReviewRequest {
  response: string;
}

export interface ReportReviewRequest {
  reason: string;
}

export interface ReviewsListResponse {
  success: boolean;
  data: ReviewListItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  analytics?: ReviewAnalytics;
}

export interface ReviewResponse {
  success: boolean;
  data: ReviewListItem;
  error?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get star rating display
 */
export function getRatingStars(rating: ReviewRating): string {
  return '\u2605'.repeat(rating) + '\u2606'.repeat(5 - rating);
}

/**
 * Format review date for display
 */
export function formatReviewDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}

/**
 * Calculate rating badge variant
 */
export function getRatingBadgeVariant(rating: ReviewRating): 'success' | 'warning' | 'destructive' {
  if (rating >= 4) return 'success';
  if (rating >= 3) return 'warning';
  return 'destructive';
}

/**
 * Calculate average rating from distribution
 */
export function calculateAverageRating(distribution: Record<ReviewRating, number>): number {
  let totalWeight = 0;
  let totalCount = 0;

  for (const [rating, count] of Object.entries(distribution)) {
    totalWeight += Number(rating) * count;
    totalCount += count;
  }

  return totalCount > 0 ? totalWeight / totalCount : 0;
}

/**
 * Calculate analytics from reviews
 */
export function calculateReviewAnalytics(reviews: ReviewListItem[]): ReviewAnalytics {
  const distribution: Record<ReviewRating, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const byStatus: Record<ReviewStatus, number> = {
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    REPORTED: 0,
  };

  let totalRating = 0;
  let respondedCount = 0;
  let totalResponseTimeMs = 0;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  let recentReviews = 0;
  let recentTotalRating = 0;

  for (const review of reviews) {
    // Ensure rating is within valid range for distribution
    const ratingKey = Math.min(5, Math.max(1, Math.round(review.rating))) as ReviewRating;
    distribution[ratingKey]++;
    byStatus[review.status]++;
    totalRating += review.rating;

    // Check if review has a response
    const hasResponse = review.businessResponse !== null;
    if (hasResponse && review.respondedAt) {
      respondedCount++;
      totalResponseTimeMs += review.respondedAt.getTime() - review.createdAt.getTime();
    }

    if (review.createdAt >= thirtyDaysAgo) {
      recentReviews++;
      recentTotalRating += review.rating;
    }
  }

  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 ? totalRating / totalReviews : 0;
  const recentAverageRating = recentReviews > 0 ? recentTotalRating / recentReviews : 0;

  // Determine trend
  let ratingTrend: 'up' | 'down' | 'stable' = 'stable';
  if (recentReviews > 0 && totalReviews > recentReviews) {
    const olderAvg = (totalRating - recentTotalRating) / (totalReviews - recentReviews);
    if (recentAverageRating > olderAvg + 0.2) ratingTrend = 'up';
    else if (recentAverageRating < olderAvg - 0.2) ratingTrend = 'down';
  }

  return {
    totalReviews,
    averageRating: Math.round(averageRating * 10) / 10,
    ratingDistribution: distribution,
    totalResponded: respondedCount,
    responseRate: totalReviews > 0 ? respondedCount / totalReviews : 0,
    avgResponseTimeHours: respondedCount > 0
      ? Math.round(totalResponseTimeMs / respondedCount / (60 * 60 * 1000))
      : 0,
    recentReviews,
    recentAverageRating: Math.round(recentAverageRating * 10) / 10,
    ratingTrend,
    byStatus,
  };
}
